import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Exporta un elemento DOM a PNG
 * @param {HTMLElement} element - Elemento a exportar
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @param {Object} options - Opciones adicionales
 */
export const exportToPNG = async (element, filename = 'sankey-chart', options = {}) => {
  try {
    // Esperar un momento para asegurar que la gráfica esté completamente renderizada
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log('=== DIAGNÓSTICO DE EXPORTACIÓN PNG ===');
    console.log('Elemento original:', element);
    console.log('Dimensiones del elemento:', {
      offsetWidth: element.offsetWidth,
      offsetHeight: element.offsetHeight,
      scrollWidth: element.scrollWidth,
      scrollHeight: element.scrollHeight,
      clientWidth: element.clientWidth,
      clientHeight: element.clientHeight
    });
    
    // Buscar el SVG dentro del elemento
    const svgElement = element.querySelector('svg');
    console.log('SVG encontrado:', svgElement);
    if (svgElement) {
      console.log('Dimensiones del SVG:', {
        width: svgElement.getAttribute('width'),
        height: svgElement.getAttribute('height'),
        offsetWidth: svgElement.offsetWidth,
        offsetHeight: svgElement.offsetHeight
      });
    }
    
    // Buscar todos los elementos gráficos
    const allGraphics = element.querySelectorAll('svg, canvas, img');
    console.log('Elementos gráficos encontrados:', allGraphics.length);
    
    const defaultOptions = {
      backgroundColor: '#ffffff',
      scale: 1, // Reducir escala inicialmente
      useCORS: true,
      allowTaint: true,
      foreignObjectRendering: true, // Cambiar a true
      logging: true,
      width: element.offsetWidth,
      height: element.offsetHeight,
      x: 0,
      y: 0,
      ignoreElements: (element) => {
        // Ignorar elementos problemáticos
        return element.tagName === 'IFRAME' || 
               element.classList?.contains('tooltip') ||
               element.classList?.contains('ant-tooltip');
      },
      onclone: (clonedDoc, element) => {
        console.log('Documento clonado:', clonedDoc);
        console.log('Elemento clonado:', element);
        
        // Asegurar que todos los SVGs sean visibles
        const svgs = element.querySelectorAll('svg');
        svgs.forEach(svg => {
          svg.style.display = 'block';
          svg.style.visibility = 'visible';
        });
        
        return element;
      },
      ...options
    };

    console.log('Opciones de captura:', defaultOptions);

    // Crear el canvas
    const canvas = await html2canvas(element, defaultOptions);
    
    console.log('Canvas creado:', {
      width: canvas.width,
      height: canvas.height,
      hasContent: canvas.width > 0 && canvas.height > 0
    });
    
    // Verificar si el canvas tiene contenido
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('El canvas generado está vacío. Verifica que el elemento tenga contenido visible.');
    }
    
    // Crear enlace de descarga
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    const dataURL = canvas.toDataURL('image/png');
    link.href = dataURL;
    
    console.log('Data URL generado (primeros 100 chars):', dataURL.substring(0, 100));
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return { success: true, message: 'Imagen PNG exportada correctamente' };
  } catch (error) {
    console.error('Error al exportar PNG:', error);
    return { success: false, message: 'Error al exportar la imagen PNG', error };
  }
};

/**
 * Exporta un elemento DOM a PDF
 * @param {HTMLElement} element - Elemento a exportar
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @param {Object} options - Opciones adicionales
 */
export const exportToPDF = async (element, filename = 'sankey-chart', options = {}) => {
  try {
    // Esperar un momento para asegurar que la gráfica esté completamente renderizada
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const defaultCanvasOptions = {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: true, // Permitir contenido que podría estar 'tainted'
      foreignObjectRendering: false, // Desactivar para mejor compatibilidad con SVG
      logging: true, // Activar logs para debug
      width: element.scrollWidth || element.offsetWidth,
      height: element.scrollHeight || element.offsetHeight,
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: 0,
      windowWidth: element.scrollWidth || element.offsetWidth,
      windowHeight: element.scrollHeight || element.offsetHeight,
      ...options.canvas
    };

    console.log('Iniciando captura PDF con opciones:', defaultCanvasOptions);

    // Crear el canvas
    const canvas = await html2canvas(element, defaultCanvasOptions);
    
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    
    // Calcular dimensiones del PDF
    const pdfWidth = options.pdfWidth || 297; // A4 landscape width in mm
    const pdfHeight = options.pdfHeight || 210; // A4 landscape height in mm
    
    // Calcular escala para ajustar la imagen al PDF
    const scaleX = (pdfWidth - 20) / (imgWidth * 0.264583); // 20mm margin, convert px to mm
    const scaleY = (pdfHeight - 20) / (imgHeight * 0.264583);
    const scale = Math.min(scaleX, scaleY, 1); // No aumentar más del tamaño original
    
    const finalWidth = (imgWidth * 0.264583) * scale;
    const finalHeight = (imgHeight * 0.264583) * scale;
    
    // Centrar la imagen
    const xOffset = (pdfWidth - finalWidth) / 2;
    const yOffset = (pdfHeight - finalHeight) / 2;
    
    // Crear PDF
    const pdf = new jsPDF({
      orientation: finalWidth > finalHeight ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Agregar título si se proporciona
    if (options.title) {
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text(options.title, pdfWidth / 2, 15, { align: 'center' });
    }
    
    // Agregar fecha
    const currentDate = new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    pdf.text(`Generado el: ${currentDate}`, pdfWidth - 10, pdfHeight - 5, { align: 'right' });
    
    // Agregar la imagen
    const imageY = options.title ? Math.max(yOffset, 25) : yOffset;
    pdf.addImage(imgData, 'PNG', xOffset, imageY, finalWidth, finalHeight);
    
    // Guardar PDF
    pdf.save(`${filename}.pdf`);
    
    return { success: true, message: 'PDF exportado correctamente' };
  } catch (error) {
    console.error('Error al exportar PDF:', error);
    return { success: false, message: 'Error al exportar el PDF', error };
  }
};

/**
 * Exporta específicamente el SVG de Google Charts a PNG
 * @param {HTMLElement} element - Elemento contenedor
 * @param {string} filename - Nombre del archivo
 * @param {Object} options - Opciones adicionales
 */
export const exportGoogleChartToPNG = async (element, filename = 'sankey-chart', options = {}) => {
  try {
    console.log('=== EXPORTACIÓN ESPECÍFICA GOOGLE CHARTS ===');
    
    // Buscar específicamente el SVG de Google Charts
    const svgElement = element.querySelector('svg');
    
    if (!svgElement) {
      throw new Error('No se encontró el SVG de la gráfica');
    }
    
    console.log('SVG encontrado:', svgElement);
    console.log('Dimensiones SVG:', {
      width: svgElement.getAttribute('width'),
      height: svgElement.getAttribute('height'),
      offsetWidth: svgElement.offsetWidth,
      offsetHeight: svgElement.offsetHeight,
      viewBox: svgElement.getAttribute('viewBox')
    });
    
    // Crear un contenedor temporal específico para el SVG
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.background = '#ffffff';
    tempContainer.style.padding = '20px';
    tempContainer.style.display = 'block';
    
    // Clonar el SVG
    const clonedSVG = svgElement.cloneNode(true);
    
    // Asegurar que el SVG tenga dimensiones explícitas
    if (!clonedSVG.getAttribute('width')) {
      clonedSVG.setAttribute('width', svgElement.offsetWidth || 800);
    }
    if (!clonedSVG.getAttribute('height')) {
      clonedSVG.setAttribute('height', svgElement.offsetHeight || 600);
    }
    
    tempContainer.appendChild(clonedSVG);
    document.body.appendChild(tempContainer);
    
    try {
      const defaultOptions = {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: false,
        logging: true,
        width: tempContainer.offsetWidth,
        height: tempContainer.offsetHeight,
        ...options
      };
      
      console.log('Capturando contenedor temporal:', tempContainer);
      
      const canvas = await html2canvas(tempContainer, defaultOptions);
      
      console.log('Canvas temporal creado:', {
        width: canvas.width,
        height: canvas.height
      });
      
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('El canvas generado está vacío');
      }
      
      // Crear enlace de descarga
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL('image/png');
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      return { success: true, message: 'Imagen PNG exportada correctamente (método alternativo)' };
      
    } finally {
      // Limpiar el contenedor temporal
      document.body.removeChild(tempContainer);
    }
    
  } catch (error) {
    console.error('Error en exportación alternativa:', error);
    return { success: false, message: 'Error en exportación alternativa', error };
  }
};

/**
 * Convierte SVG directamente a PNG (método nativo)
 * @param {HTMLElement} element - Elemento contenedor
 * @param {string} filename - Nombre del archivo
 */
export const exportSVGDirectToPNG = async (element, filename = 'sankey-chart') => {
  try {
    console.log('=== EXPORTACIÓN DIRECTA SVG ===');
    
    const svgElement = element.querySelector('svg');
    if (!svgElement) {
      throw new Error('No se encontró el SVG');
    }
    
    // Obtener dimensiones
    const width = svgElement.offsetWidth || parseInt(svgElement.getAttribute('width')) || 800;
    const height = svgElement.offsetHeight || parseInt(svgElement.getAttribute('height')) || 600;
    
    console.log('Dimensiones para exportación:', { width, height });
    
    // Serializar el SVG
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    
    console.log('SVG serializado (primeros 200 chars):', svgString.substring(0, 200));
    
    // Crear un blob del SVG
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    // Crear canvas
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; // Escala para mejor calidad
    canvas.height = height * 2;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(2, 2);
    
    // Crear imagen desde SVG
    const img = new Image();
    
    return new Promise((resolve, reject) => {
      img.onload = () => {
        try {
          ctx.drawImage(img, 0, 0, width, height);
          
          // Crear enlace de descarga
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.download = `${filename}.png`;
              link.href = url;
              
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              URL.revokeObjectURL(url);
              URL.revokeObjectURL(svgUrl);
              
              resolve({ success: true, message: 'Imagen PNG exportada correctamente (método directo SVG)' });
            } else {
              reject(new Error('No se pudo crear el blob'));
            }
          }, 'image/png');
          
        } catch (error) {
          URL.revokeObjectURL(svgUrl);
          reject(error);
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        reject(new Error('Error al cargar la imagen SVG'));
      };
      
      img.src = svgUrl;
    });
    
  } catch (error) {
    console.error('Error en exportación directa SVG:', error);
    return { success: false, message: 'Error en exportación directa SVG', error };
  }
};

/**
 * Exporta SVG directamente a PDF
 * @param {HTMLElement} element - Elemento contenedor
 * @param {string} filename - Nombre del archivo
 * @param {Object} options - Opciones del PDF
 */
export const exportSVGDirectToPDF = async (element, filename = 'sankey-chart', options = {}) => {
  try {
    console.log('=== EXPORTACIÓN DIRECTA SVG A PDF ===');
    
    const svgElement = element.querySelector('svg');
    if (!svgElement) {
      throw new Error('No se encontró el SVG');
    }
    
    // Obtener dimensiones
    const width = svgElement.offsetWidth || parseInt(svgElement.getAttribute('width')) || 800;
    const height = svgElement.offsetHeight || parseInt(svgElement.getAttribute('height')) || 600;
    
    console.log('Dimensiones para PDF:', { width, height });
    
    // Serializar el SVG
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    
    // Crear un blob del SVG
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    // Crear canvas para generar imagen
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; // Escala para mejor calidad
    canvas.height = height * 2;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(2, 2);
    
    // Crear imagen desde SVG
    const img = new Image();
    
    return new Promise((resolve, reject) => {
      img.onload = async () => {
        try {
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convertir canvas a imagen
          const imgData = canvas.toDataURL('image/png');
          
          // Crear PDF
          const { default: jsPDF } = await import('jspdf');
          
          // Calcular dimensiones del PDF
          const pdfWidth = options.pdfWidth || 297; // A4 landscape width in mm
          const pdfHeight = options.pdfHeight || 210; // A4 landscape height in mm
          
          // Calcular escala para ajustar la imagen al PDF
          const scaleX = (pdfWidth - 40) / (width * 0.264583); // 40mm margin, convert px to mm
          const scaleY = (pdfHeight - 60) / (height * 0.264583); // 60mm margin para título
          const scale = Math.min(scaleX, scaleY, 1); // No aumentar más del tamaño original
          
          const finalWidth = (width * 0.264583) * scale;
          const finalHeight = (height * 0.264583) * scale;
          
          // Centrar la imagen
          const xOffset = (pdfWidth - finalWidth) / 2;
          const yOffset = (pdfHeight - finalHeight) / 2 + 30; // +30 para espacio del título
          
          // Crear PDF
          const pdf = new jsPDF({
            orientation: finalWidth > finalHeight ? 'landscape' : 'portrait',
            unit: 'mm',
            format: 'a4'
          });
          
          // Agregar título si se proporciona
          if (options.title) {
            pdf.setFontSize(16);
            pdf.setFont(undefined, 'bold');
            pdf.text(options.title, pdfWidth / 2, 20, { align: 'center' });
          }
          
          // Agregar fecha
          const currentDate = new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
          
          pdf.setFontSize(10);
          pdf.setFont(undefined, 'normal');
          pdf.text(`Generado el: ${currentDate}`, pdfWidth - 10, pdfHeight - 5, { align: 'right' });
          
          // Agregar la imagen
          pdf.addImage(imgData, 'PNG', xOffset, yOffset, finalWidth, finalHeight);
          
          // Guardar PDF
          pdf.save(`${filename}.pdf`);
          
          URL.revokeObjectURL(svgUrl);
          resolve({ success: true, message: 'PDF exportado correctamente (método directo SVG)' });
          
        } catch (error) {
          URL.revokeObjectURL(svgUrl);
          reject(error);
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        reject(new Error('Error al cargar la imagen SVG para PDF'));
      };
      
      img.src = svgUrl;
    });
    
  } catch (error) {
    console.error('Error en exportación directa SVG a PDF:', error);
    return { success: false, message: 'Error en exportación directa SVG a PDF', error };
  }
};

/**
 * Exporta usando el método de contenedor temporal a PDF
 * @param {HTMLElement} element - Elemento contenedor
 * @param {string} filename - Nombre del archivo
 * @param {Object} options - Opciones del PDF
 */
export const exportGoogleChartToPDF = async (element, filename = 'sankey-chart', options = {}) => {
  try {
    console.log('=== EXPORTACIÓN PDF CON CONTENEDOR TEMPORAL ===');
    
    // Buscar específicamente el SVG de Google Charts
    const svgElement = element.querySelector('svg');
    
    if (!svgElement) {
      throw new Error('No se encontró el SVG de la gráfica para PDF');
    }
    
    // Crear un contenedor temporal específico para el SVG
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.background = '#ffffff';
    tempContainer.style.padding = '20px';
    tempContainer.style.display = 'block';
    
    // Clonar el SVG
    const clonedSVG = svgElement.cloneNode(true);
    
    // Asegurar que el SVG tenga dimensiones explícitas
    if (!clonedSVG.getAttribute('width')) {
      clonedSVG.setAttribute('width', svgElement.offsetWidth || 800);
    }
    if (!clonedSVG.getAttribute('height')) {
      clonedSVG.setAttribute('height', svgElement.offsetHeight || 600);
    }
    
    tempContainer.appendChild(clonedSVG);
    document.body.appendChild(tempContainer);
    
    try {
      // Esperar un momento para que el DOM se actualice
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const defaultCanvasOptions = {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: false,
        logging: true,
        width: tempContainer.offsetWidth,
        height: tempContainer.offsetHeight
      };
      
      console.log('Capturando contenedor temporal para PDF:', tempContainer);
      
      const canvas = await html2canvas(tempContainer, defaultCanvasOptions);
      
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('El canvas para PDF está vacío');
      }
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Crear PDF usando jsPDF
      const { default: jsPDF } = await import('jspdf');
      
      // Calcular dimensiones del PDF
      const pdfWidth = options.pdfWidth || 297; // A4 landscape width in mm
      const pdfHeight = options.pdfHeight || 210; // A4 landscape height in mm
      
      // Calcular escala para ajustar la imagen al PDF
      const scaleX = (pdfWidth - 40) / (imgWidth * 0.264583); // 40mm margin
      const scaleY = (pdfHeight - 60) / (imgHeight * 0.264583); // 60mm margin
      const scale = Math.min(scaleX, scaleY, 1);
      
      const finalWidth = (imgWidth * 0.264583) * scale;
      const finalHeight = (imgHeight * 0.264583) * scale;
      
      // Centrar la imagen
      const xOffset = (pdfWidth - finalWidth) / 2;
      const yOffset = (pdfHeight - finalHeight) / 2 + 30;
      
      // Crear PDF
      const pdf = new jsPDF({
        orientation: finalWidth > finalHeight ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Agregar título
      if (options.title) {
        pdf.setFontSize(16);
        pdf.setFont(undefined, 'bold');
        pdf.text(options.title, pdfWidth / 2, 20, { align: 'center' });
      }
      
      // Agregar fecha
      const currentDate = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Generado el: ${currentDate}`, pdfWidth - 10, pdfHeight - 5, { align: 'right' });
      
      // Agregar la imagen
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, finalWidth, finalHeight);
      
      // Guardar PDF
      pdf.save(`${filename}.pdf`);
      
      return { success: true, message: 'PDF exportado correctamente (método contenedor temporal)' };
      
    } finally {
      // Limpiar el contenedor temporal
      document.body.removeChild(tempContainer);
    }
    
  } catch (error) {
    console.error('Error en exportación PDF con contenedor temporal:', error);
    return { success: false, message: 'Error en exportación PDF con contenedor temporal', error };
  }
};

/**
 * Genera un nombre de archivo único basado en la fecha y hora actual
 * @param {string} prefix - Prefijo del nombre del archivo
 * @param {string} type - Tipo de análisis (proyecto, elemento, tarjeta)
 * @param {string} name - Nombre del elemento analizado
 * @returns {string} Nombre de archivo único
 */
export const generateFileName = (prefix = 'sankey', type = '', name = '') => {
  const now = new Date();
  const timestamp = now.toISOString()
    .replace(/[:.]/g, '-')
    .replace('T', '_')
    .substring(0, 19);
  
  let filename = `${prefix}_${timestamp}`;
  
  if (type && name) {
    const cleanName = name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
    filename = `${prefix}_${type}_${cleanName}_${timestamp}`;
  }
  
  return filename;
};

/**
 * Valida si el elemento es apto para exportación
 * @param {HTMLElement} element - Elemento a validar
 * @returns {Object} Resultado de la validación
 */
export const validateElementForExport = (element) => {
  if (!element) {
    return { valid: false, message: 'No se encontró el elemento a exportar' };
  }
  
  if (element.offsetWidth === 0 || element.offsetHeight === 0) {
    return { valid: false, message: 'El elemento no tiene dimensiones válidas' };
  }
  
  // Verificar específicamente por SVG de Google Charts
  const svgElement = element.querySelector('svg');
  const canvasElement = element.querySelector('canvas');
  const googleChartsDiv = element.querySelector('div[dir="ltr"]');
  
  if (!svgElement && !canvasElement && !googleChartsDiv) {
    console.log('Elemento encontrado pero sin gráfica:', element);
    console.log('Contenido HTML del elemento:', element.innerHTML.substring(0, 500));
    return { valid: false, message: 'No se encontró una gráfica para exportar. Asegúrate de que la gráfica esté completamente cargada.' };
  }
  
  const chartElement = svgElement || canvasElement || googleChartsDiv;
  
  console.log('=== VALIDACIÓN DE ELEMENTO ===');
  console.log('Elemento contenedor:', {
    width: element.offsetWidth,
    height: element.offsetHeight,
    className: element.className,
    tagName: element.tagName
  });
  
  console.log('Elemento gráfico encontrado:', {
    tagName: chartElement.tagName,
    width: chartElement.offsetWidth || chartElement.getAttribute('width'),
    height: chartElement.offsetHeight || chartElement.getAttribute('height'),
    hasContent: chartElement.children.length > 0 || chartElement.innerHTML.length > 0
  });
  
  return { valid: true, message: 'Elemento válido para exportación', chartElement };
};

/**
 * Configuraciones predefinidas para diferentes tipos de exportación
 */
export const exportConfigs = {
  sankey: {
    png: {
      canvas: {
        backgroundColor: '#ffffff',
        scale: 3,
        useCORS: true,
        allowTaint: false,
        foreignObjectRendering: true,
      }
    },
    pdf: {
      canvas: {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: false,
        foreignObjectRendering: true,
      },
      pdfWidth: 297,
      pdfHeight: 210,
      title: 'Gráfica Sankey - Análisis de Flujo de Datos'
    }
  }
};